import { useEffect, useRef, useCallback } from "react";

type UseOptimizedPollingOptions = {
  interval: number; // in seconds
  maxRetries?: number;
  baseDelay?: number; // in seconds
  maxDelay?: number; // in seconds
  onPoll: () => Promise<void>;
  enabled?: boolean;
};

/**
 * Custom hook for optimized polling with exponential backoff
 * - Waits for previous request to complete before starting next
 * - Uses exponential backoff for failed requests
 * - Configurable intervals and retry limits
 */
function useOptimizedPolling({
  interval = 10,
  maxRetries = 5,
  baseDelay = 1,
  maxDelay = 30,
  onPoll,
  enabled = true,
}: UseOptimizedPollingOptions) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);
  const retryCountRef = useRef(0);
  const mountedRef = useRef(true);

  const clearCurrentTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const calculateBackoffDelay = useCallback(
    (retryCount: number) => {
      const delayInSeconds = Math.min(baseDelay * 2 ** retryCount, maxDelay);
      // Add jitter (±25% randomization)
      const jitter = delayInSeconds * 0.25 * (Math.random() - 0.5);
      return Math.max(0.01, delayInSeconds + jitter); // Minimum 10ms
    },
    [baseDelay, maxDelay],
  );

  const pollDataRef = useRef<() => Promise<void>>();

  const scheduleNextPoll = useCallback(
    (delayInSeconds: number) => {
      clearCurrentTimeout();
      timeoutRef.current = setTimeout(() => {
        pollDataRef.current?.().catch(() => {
          // Error handling is done within pollData
        });
      }, delayInSeconds * 1000);
    },
    [clearCurrentTimeout],
  );

  const pollData = useCallback(async () => {
    if (!mountedRef.current || !enabled || isPollingRef.current) {
      return;
    }

    isPollingRef.current = true;

    try {
      await onPoll();
      retryCountRef.current = 0;
      scheduleNextPoll(interval);
    } catch (error) {
      // Log error for debugging/monitoring purposes
      // eslint-disable-next-line no-console
      console.error("[useOptimizedPolling] Poll failed:", error);

      retryCountRef.current += 1;

      if (retryCountRef.current <= maxRetries) {
        const backoffDelay = calculateBackoffDelay(retryCountRef.current - 1);
        scheduleNextPoll(backoffDelay);
      } else {
        retryCountRef.current = 0;
        scheduleNextPoll(interval);
      }
    } finally {
      isPollingRef.current = false;
    }
  }, [
    onPoll,
    enabled,
    interval,
    maxRetries,
    calculateBackoffDelay,
    scheduleNextPoll,
  ]);

  // Keep the ref up to date
  pollDataRef.current = pollData;

  // Start polling when hook is initialized or when enabled changes
  useEffect(() => {
    if (enabled && mountedRef.current) {
      // Start first poll immediately
      pollData().catch(() => {
        // Error handling is done within pollData
      });
    }

    return clearCurrentTimeout;
  }, [enabled, pollData, clearCurrentTimeout]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      clearCurrentTimeout();
    };
  }, [clearCurrentTimeout]);

  // Return function to manually trigger a poll (useful for testing or manual refresh)
  return useCallback(() => {
    if (!isPollingRef.current) {
      pollData().catch(() => {
        // Error handling is done within pollData
      });
    }
  }, [pollData]);
}

export default useOptimizedPolling;
